import React, { useState } from 'react';
import { add_address_book_entry } from '../../api/customers';

export const emptyAddressEndpoint = () => ({
    address_book_id: null,
    name: '',
    address: '',
    postcode: '',
    additional_info: '',
});

export const endpointFromAddress = (address) => address ? ({
    address_book_id: address.id,
    name: address.company || '',
    address: address.address || '',
    postcode: address.postcode || '',
    additional_info: address.additional_info || '',
}) : emptyAddressEndpoint();

export const addressSnapshotFields = (collection, delivery) => ({
    collection_address_id: collection.address_book_id,
    collection_name: collection.name.trim(),
    collection_address: collection.address.trim(),
    collection_postcode: collection.postcode.trim().toUpperCase(),
    collection_additional_info: collection.additional_info.trim() || null,
    delivery_address_id: delivery.address_book_id,
    delivery_name: delivery.name.trim(),
    delivery_address: delivery.address.trim(),
    delivery_postcode: delivery.postcode.trim().toUpperCase(),
    delivery_additional_info: delivery.additional_info.trim() || null,
});

export const isCompleteAddress = (value) => Boolean(
    value.name.trim() && value.address.trim() && value.postcode.trim()
);

const normalize = (text) => (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
const normalizePostcode = (text) => normalize(text).replace(/\s/g, '');

const AddressEndpointFields = ({ label, addresses, value, onChange, disabled = false, canSaveAddress = false, onAddressSaved }) => {
    const [saveOpen, setSaveOpen] = useState(false);
    const [friendlyName, setFriendlyName] = useState('');
    const [saveError, setSaveError] = useState('');
    const [serverDuplicate, setServerDuplicate] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const selectAddress = (selectedValue) => {
        if (selectedValue === 'manual') {
            onChange(emptyAddressEndpoint());
            return;
        }
        const selected = addresses.find((address) => String(address.id) === selectedValue);
        onChange(endpointFromAddress(selected));
    };

    const isSaved = Boolean(value.address_book_id);
    const companyMatches = addresses.filter((item) => normalize(item.company) === normalize(value.name));
    const exactMatch = serverDuplicate || companyMatches.find((item) => (
        normalize(item.address) === normalize(value.address)
        && normalizePostcode(item.postcode) === normalizePostcode(value.postcode)
    ));

    const openSave = () => {
        setFriendlyName('');
        setSaveError('');
        setServerDuplicate(null);
        setSaveOpen(true);
    };

    const selectExisting = (address) => {
        onChange(endpointFromAddress(address));
        setSaveOpen(false);
    };

    const saveAddress = async () => {
        if (!friendlyName.trim()) {
            setSaveError('Friendly name is required.');
            return;
        }

        setIsSaving(true);
        setSaveError('');
        const result = await add_address_book_entry({
            friendly_name: friendlyName.trim(),
            company: value.name.trim(),
            address: value.address.trim(),
            postcode: value.postcode.trim().toUpperCase(),
            additional_info: value.additional_info.trim(),
        });
        setIsSaving(false);

        if (result.status === 200) {
            onAddressSaved?.(result.data);
            onChange(endpointFromAddress(result.data));
            setSaveOpen(false);
            return;
        }
        if (result.status === 409 && result.data?.existing) {
            setServerDuplicate(result.data.existing);
            setSaveError(result.data.error);
            return;
        }
        setSaveError(result.data?.error || 'Failed to save address.');
    };

    return (
        <>
            <fieldset disabled={disabled} style={{ border: '1px solid rgba(128,128,128,0.35)', borderRadius: 6, padding: 12, marginBottom: 14, opacity: disabled ? 0.7 : 1 }}>
                <legend style={{ fontSize: '0.95rem', fontWeight: 700, padding: '0 6px', width: 'auto' }}>{label}</legend>
                <label htmlFor={`${label}-address-source`}>Saved address</label>
                <select id={`${label}-address-source`} className="input" value={isSaved ? String(value.address_book_id) : 'manual'} onChange={(event) => selectAddress(event.target.value)}>
                    <option value="manual">Enter manually</option>
                    {addresses.map((address) => <option key={address.id} value={address.id}>{address.friendly_name}</option>)}
                </select>
                <label htmlFor={`${label}-company`}>Company / Name *</label>
                <input id={`${label}-company`} className="input" value={value.name} disabled={isSaved} onChange={(event) => onChange({ ...value, name: event.target.value })} />
                <label htmlFor={`${label}-address`}>Address *</label>
                <input id={`${label}-address`} className="input" value={value.address} disabled={isSaved} onChange={(event) => onChange({ ...value, address: event.target.value })} />
                <label htmlFor={`${label}-postcode`}>Postcode *</label>
                <input id={`${label}-postcode`} className="input" value={value.postcode} disabled={isSaved} onChange={(event) => onChange({ ...value, postcode: event.target.value })} />
                <label htmlFor={`${label}-additional-info`}>Additional Information</label>
                <textarea id={`${label}-additional-info`} className="input" rows={2} value={value.additional_info} disabled={isSaved} onChange={(event) => onChange({ ...value, additional_info: event.target.value })} />
                {canSaveAddress && !isSaved && (
                    <button type="button" className="btn btn-outline-primary" disabled={!isCompleteAddress(value)} onClick={openSave} style={{ marginTop: 10 }}>
                        Add to Address Book
                    </button>
                )}
            </fieldset>

            {saveOpen && (
                <div className="overlay" style={{ zIndex: 100010 }} onMouseDown={(event) => event.stopPropagation()}>
                    <div className="modal-container" style={{ maxWidth: 560 }} onKeyDown={(event) => event.stopPropagation()}>
                        <h2>Save {label} Address</h2>
                        <div style={{ marginBottom: 16 }}>
                            <strong>{value.name}</strong>
                            <div>{value.address}</div>
                            <div>{value.postcode.toUpperCase()}</div>
                            {value.additional_info && <div style={{ marginTop: 6, opacity: 0.7 }}>{value.additional_info}</div>}
                        </div>

                        {exactMatch ? (
                            <div style={{ border: '1px solid #d39e00', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                                <strong>Already saved as {exactMatch.friendly_name}</strong>
                                <div>{exactMatch.company}</div>
                                <div>{exactMatch.address}, {exactMatch.postcode}</div>
                                {exactMatch.additional_info && <div style={{ marginTop: 4 }}>{exactMatch.additional_info}</div>}
                            </div>
                        ) : (
                            <>
                                {companyMatches.length > 0 && (
                                    <div style={{ border: '1px solid #d39e00', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                                        <strong>{companyMatches.length} existing {value.name} address{companyMatches.length === 1 ? '' : 'es'}:</strong>
                                        {companyMatches.map((item) => <div key={item.id} style={{ marginTop: 6 }}>{item.friendly_name}: {item.address}, {item.postcode}</div>)}
                                    </div>
                                )}
                                <label htmlFor={`${label}-friendly-name`}>Friendly Name *</label>
                                <input id={`${label}-friendly-name`} className="input" value={friendlyName} placeholder={`${value.name} ${label}`} onChange={(event) => setFriendlyName(event.target.value)} autoFocus />
                                <p style={{ marginTop: 12 }}>Confirm that this address should be added to the shared Address Book.</p>
                            </>
                        )}

                        {saveError && <p className="error-message">{saveError}</p>}
                        <div className="button-group">
                            {exactMatch ? (
                                <button type="button" className="submit-button" onClick={() => selectExisting(exactMatch)}>Use Existing Address</button>
                            ) : (
                                <button type="button" className="submit-button" disabled={isSaving} onClick={saveAddress}>{isSaving ? 'Saving...' : 'Confirm and Add'}</button>
                            )}
                            <button type="button" className="close-button" onClick={() => setSaveOpen(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AddressEndpointFields;